import { Button } from '@/components/Button';
import { Divider } from '@/components/Divider';
import { Switch } from '@/components/Switch';
import Text from '@/components/Text';
import { DataStreamBaseTileConfig, type ShapeName } from '@squaredup/data-streams';
import { isDefined } from '@squaredup/utilities';
import { ModalButtons } from 'components/Modal';
import { omit } from 'lodash';
import { Dispatch, MutableRefObject, PropsWithChildren, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { DataStreamMetadataColumnFields, toFormData } from '../MetadataEditorForm';
import { overrideMetadataStateFromFormData, type DataStreamMetadataAction } from '../MetadataEditorReducer';
import { metadataEditorShapesByName } from '../MetadataEditorShapes';
import {
    type CloneStateColumn,
    type ComparisonStateColumn,
    type ComputedStateColumn,
    type DataStreamMetadataState,
    type StateColumn
} from '../MetadataEditorState';
import { MetadataTableRow } from '../MetadataEditorTable';
import { toTileMetadata } from '../MetadataEditorTileState';
import { ShapeConfigField } from '../ShapeConfigField';
import { FormatExpressionExamples } from './FormatExpressionExamples';
import { FormatExpressionField } from './FormatExpressionField';
import { PreviewSampleValue } from './PreviewSampleValue';
import { ValueExpressionField } from './ValueExpressionField';
import { ValueExpressionExamples } from './ValueExpressionExamples';

const BASIC = 'Basic';
const CUSTOM = 'Custom';

interface DefaultExpressions {
    formatExpression?: string;
    valueExpression?: string;
}

interface ShapeConfigFormProps {
    row: MetadataTableRow;
    columns: (StateColumn | CloneStateColumn | ComparisonStateColumn | ComputedStateColumn)[];
    columnShape: ShapeName;
    columnDisplayName: string;
    stateCopy: DataStreamMetadataState;
    config: DataStreamBaseTileConfig;
    defaultExpressions: MutableRefObject<DefaultExpressions>;
    dispatch: Dispatch<DataStreamMetadataAction>;
    onClose: () => void;
}

type FormFields = DataStreamMetadataColumnFields & {
    mode: typeof BASIC | typeof CUSTOM;
};

export const ShapeConfigForm: React.FC<PropsWithChildren<ShapeConfigFormProps>> = ({
    row,
    columns,
    columnShape,
    columnDisplayName,
    stateCopy,
    config,
    defaultExpressions,
    children,
    dispatch,
    onClose
}) => {
    const column = columns.find(({ columnName }) => row.name === columnName);
    const defaultValues = toFormData(stateCopy)[row.name];

    const form = useForm<FormFields>({
        mode: 'all',
        defaultValues: {
            ...metadataEditorShapesByName.get(columnShape)?.defaultFormValues,
            ...defaultValues.shapeConfig,
            valueExpression: defaultExpressions.current.valueExpression || defaultValues.valueExpression,
            formatExpression: defaultExpressions.current.formatExpression || defaultValues.formatExpression,
            mode:
                column?.metadataOverrides?.formatExpression || defaultExpressions.current.formatExpression
                    ? CUSTOM
                    : BASIC
        }
    });

    const [isMonacoLoaded, setIsMonacoLoaded] = useState(false);
    const shapeMode = form.watch('mode');

    // Ensure we're tracking any changed format and/or value expressions
    form.watch(({ formatExpression, valueExpression, mode }) => {
        if (formatExpression !== undefined) {
            defaultExpressions.current = {
                ...defaultExpressions.current,
                formatExpression
            };
        }
        if (valueExpression !== undefined) {
            defaultExpressions.current = {
                ...defaultExpressions.current,
                valueExpression
            };
        }

        // If the user select basic we want to remove the format expression, else it will always revert when the user
        // when they change shape
        if (mode === BASIC) {
            defaultExpressions.current = {
                ...defaultExpressions.current,
                formatExpression: undefined
            };
        }
    });

    const onSubmit: SubmitHandler<FormFields> = ({ mode, valueExpression, formatExpression, ...data }) => {
        dispatch({
            type: 'overrideMetadata',
            formData: {
                [row.name]: {
                    shape: columnShape,
                    valueExpression,
                    displayName: columnDisplayName,
                    shapeConfig: mode === BASIC ? data : undefined,
                    formatExpression: mode === CUSTOM ? formatExpression : undefined
                }
            }
        });

        onClose();
    };

    return (
        <>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col flex-1 min-h-0'>
                    <div className='flex-1 min-h-0 px-8 scroll-overflow'>
                        {children}

                        {row.isComputed && (
                            <>
                                <div className='relative'>
                                    <ValueExpressionField
                                        columnName={row.name}
                                        columns={columns
                                            .map((c) => c.metadata!)
                                            .filter((c) => c && c.role !== 'computed' && c.role !== 'comparison')}
                                    />
                                </div>                                
                                <ValueExpressionExamples />
                                <Divider className='w-full h-px my-6 bg-dividerPrimary' />
                            </>
                        )}

                        <Text.Body className='mb-2 text-base font-medium text-textPrimary'>Formatting</Text.Body>

                        <PreviewSampleValue
                            config={config}
                            columnName={row.name}
                            columnShape={columnShape}
                            metadata={toTileMetadata(
                                overrideMetadataStateFromFormData(stateCopy, {
                                    [row.name]: {
                                        shape: columnShape,
                                        valueExpression: form.watch('valueExpression'),
                                        formatExpression:
                                            shapeMode === CUSTOM ? form.watch('formatExpression') : undefined,
                                        shapeConfig:
                                            shapeMode === BASIC ? omit(form.watch(), 'formatExpression') : undefined
                                    }
                                })
                            )}
                        />

                        <Controller
                            name='mode'
                            render={({ field: { value, onChange } }) => (
                                <Switch
                                    className='w-full mb-6'
                                    itemClassName='inline w-full'
                                    value={value}
                                    onValueChange={(newValue) => {
                                        onChange(newValue);
                                        setIsMonacoLoaded(false);
                                    }}
                                    options={[
                                        { value: BASIC, label: BASIC },
                                        { value: CUSTOM, label: CUSTOM }
                                    ]}
                                />
                            )}
                        />

                        {shapeMode === BASIC && (
                            <div>
                                <ShapeConfigField
                                    shapeName={columnShape}
                                    state={stateCopy.columnMap.get(column!.columnName)}
                                />
                            </div>
                        )}

                        {shapeMode === CUSTOM && (
                            <div className='relative z-50'>
                                <FormatExpressionField
                                    columnName={row.name}
                                    columns={columns.map((c) => c.metadata).filter(isDefined)}
                                    onLoad={() => setIsMonacoLoaded(true)}
                                />

                                {isMonacoLoaded && <FormatExpressionExamples />}
                            </div>
                        )}
                    </div>

                    <ModalButtons isFooter>
                        <Button
                            type='button'
                            variant='tertiary'
                            onClick={() => onClose()}
                            data-testid='cancelAddButton'
                        >
                            Cancel
                        </Button>
                        <Button type='submit' data-testid='addButton'>
                            Save
                        </Button>
                    </ModalButtons>
                </form>
            </FormProvider>
        </>
    );
};
